import * as React from "react";
import { useMutation } from "react-query";
import { ClientConfig, ClientsService, PowerBiReportDefinition } from "gen/clients";
import { Formik } from "formik";
import Drawer from "@mui/material/Drawer";
import { SelectOption } from "@mui/base";
import { DrawerHeader } from "components/DrawerHeader/DrawerHeader";
import { DrawerBody } from "components/DrawerBody/DrawerBody";
import { SnackbarApiError } from "components/SnackbarApiError/SnackbarApiError";
import { PowerBiReportForm, FieldName } from "./components/PowerBiReportForm/PowerBiReportForm";

interface Props {
  clientConfig: ClientConfig;
  powerBiReports: PowerBiReportDefinition[];
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface FormValues {
  [FieldName.NAME]: string;
  [FieldName.DESCRIPTION]: string;
  [FieldName.WORKSPACE_ID]: string;
  [FieldName.REPORT_ID]: SelectOption<string> | null;
  [FieldName.ENABLE_RLS]: boolean;
  [FieldName.DATASET_ID]: string;
  [FieldName.RLS_ROLES]: string;
  [FieldName.RLS_USERNAME]: string;
}

const parseCsv = (value: string): string[] | undefined => {
  const items = value
    .split(",")
    .map(item => item.trim())
    .filter(Boolean);
  return items.length > 0 ? items : undefined;
};

const CreatePowerBiReportPanel: React.FC<Props> = ({ clientConfig, powerBiReports, open, onClose, onSuccess }) => {
  const { mutate, error } = useMutation({
    onSuccess,
    mutationFn: ClientsService.updateClientConfig
  });

  const validate = React.useCallback((values: FormValues) => {
    const errors: Record<string, string> = {};
    if (!values[FieldName.NAME].trim()) {
      errors[FieldName.NAME] = "Report name is required";
    }
    if (!values[FieldName.WORKSPACE_ID].trim()) {
      errors[FieldName.WORKSPACE_ID] = "Workspace is required";
    }
    if (!values[FieldName.REPORT_ID]?.value?.trim()) {
      errors[FieldName.REPORT_ID] = "Report is required";
    }
    return errors;
  }, []);

  const onSubmit = React.useCallback(
    (values: FormValues) => {
      const reportIdValue = values[FieldName.REPORT_ID]?.value?.trim();
      if (!reportIdValue) {
        return;
      }

      const createdReport: PowerBiReportDefinition = {
        id: `${reportIdValue}-${new Date().getTime().toString()}`,
        name: values[FieldName.NAME].trim(),
        description: values[FieldName.DESCRIPTION].trim() || undefined,
        workspaceId: values[FieldName.WORKSPACE_ID].trim(),
        reportId: reportIdValue,
        enableRls: values[FieldName.ENABLE_RLS] || undefined,
        datasetIds: values[FieldName.DATASET_ID] ? [values[FieldName.DATASET_ID]] : undefined,
        rlsRoles: parseCsv(values[FieldName.RLS_ROLES]),
        rlsUsername: values[FieldName.RLS_USERNAME].trim() || undefined
      };

      const updatedReports = [...(powerBiReports || []), createdReport];
      mutate({
        id: clientConfig.id,
        requestBody: { ...clientConfig, powerBiReports: updatedReports }
      });
    },
    [clientConfig, mutate, powerBiReports]
  );

  return (
    <Drawer open={open} onClose={onClose} anchor="right" PaperProps={{ sx: { width: 800 } }}>
      <DrawerHeader onClose={onClose} title="Add Power BI Report" />
      <DrawerBody>
        {!!error && <SnackbarApiError error={error} />}
        <Formik<FormValues>
          initialValues={{
            [FieldName.NAME]: "",
            [FieldName.DESCRIPTION]: "",
            [FieldName.WORKSPACE_ID]: "",
            [FieldName.REPORT_ID]: null,
            [FieldName.ENABLE_RLS]: false,
            [FieldName.DATASET_ID]: "",
            [FieldName.RLS_ROLES]: "",
            [FieldName.RLS_USERNAME]: ""
          }}
          onSubmit={onSubmit}
          validate={validate}
          validateOnChange={false}
        >
          <PowerBiReportForm onCancel={onClose} />
        </Formik>
      </DrawerBody>
    </Drawer>
  );
};

export { CreatePowerBiReportPanel };
